import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildPaidMediaPerformanceFetchKey,
  formatPaidMediaPerformanceDate,
  unwrapPaidMediaPerformancePayload,
} from "../../lib/utils/paidMediaPerformanceReport"

/**
 * GET /api/v1/report/paid/clients/{clientId}/media-performance?startDate=&endDate=
 */
export const fetchClientPaidMediaPerformance = createAsyncThunk(
  "clientPaidMediaPerformance/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatPaidMediaPerformanceDate(startDate)
      const end = formatPaidMediaPerformanceDate(endDate)
      const query = new URLSearchParams({ startDate: start, endDate: end }).toString()
      const data = await get(
        `report/paid/clients/${clientId}/media-performance?${query}`,
      )
      const normalized = unwrapPaidMediaPerformancePayload(data)

      return {
        ...normalized,
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildPaidMediaPerformanceFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch paid media performance")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate, endDate, force } = arg || {}
      if (force) return true
      const start = formatPaidMediaPerformanceDate(startDate)
      const end = formatPaidMediaPerformanceDate(endDate)
      const key = buildPaidMediaPerformanceFetchKey(clientId, start, end)
      const state = getState().clientPaidMediaPerformance
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  platformRows: [],
  summary: null,
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatPaidMediaPerformanceDate(startDate)
  const end = formatPaidMediaPerformanceDate(endDate)
  const nextKey = buildPaidMediaPerformanceFetchKey(clientId, start, end)
  const keepStale =
    nextKey === state.fetchedFetchKey &&
    Array.isArray(state.platformRows) &&
    state.platformRows.length > 0

  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStale) {
    state.fetchedFetchKey = null
    state.platformRows = []
    state.summary = null
  }
}

function isStaleFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatPaidMediaPerformanceDate(startDate)
  const end = formatPaidMediaPerformanceDate(endDate)
  const key = buildPaidMediaPerformanceFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientPaidMediaPerformanceSlice = createSlice({
  name: "clientPaidMediaPerformance",
  initialState,
  reducers: {
    clearClientPaidMediaPerformance: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientPaidMediaPerformance.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientPaidMediaPerformance.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.platformRows = action.payload?.platformRows ?? []
        state.summary = action.payload?.summary ?? null
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientPaidMediaPerformance.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch paid media performance"
        state.platformRows = []
        state.summary = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientPaidMediaPerformance } = clientPaidMediaPerformanceSlice.actions
export default clientPaidMediaPerformanceSlice.reducer
